import {
	faArrowRight as fasArrowRight,
	faCakeCandles as fasCakeCandles,
	faCalendar as fasCalendar,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { AbstractSubmitStep } from "@component/assets/AbstractLeadForm/Steps/AbstractSubmitStep";
import { Config } from "@config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import { FormCurrency, FormNumber, ProgressButton } from "bm-ui-kit";
import dayjs from "dayjs";
import type { FormApi } from "final-form";
import React from "react";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";

export interface StepFields {
	birthYear: number;
	durationYears: number;
	riskInsuranceAmount: number;
}

const MIN_AGE = 16;
const MAX_AGE = 64;
const MAX_INSURED_AGE = 80;

const MIN_DURATION_YEARS = 5;
const MAX_DURATION_YEARS = 40;

const MIN_RISK_INSURANCE_AMOUNT = 5_000_000;
const MAX_RISK_INSURANCE_AMOUNT = 100_000_000;

export class Step2 extends AbstractSubmitStep<StepFields> {
	protected initValues(): StepFields {
		const birthYear = Config.getCurrentUser().getBirthYear();

		return {
			birthYear,
			durationYears: undefined,
			riskInsuranceAmount: undefined,
		};
	}

	protected renderContent(_values: StepFields, form: FormApi<StepFields>): ReactNode {
		const { submitState } = this.props;
		const disabled = this.isDisabled();

		return (
			<>
				<Card.Body>
					<FormNumber
						label="Melyik évben születtél?"
						name="birthYear"
						maxLength={4}
						prepend={<FontAwesomeIcon icon={fasCakeCandles} />}
						validate={this.validBirthYear}
						disabled={disabled}
						required
					/>
					<FormNumber
						label="Hány évig van szükséged védelemre?"
						name="durationYears"
						prepend={<FontAwesomeIcon icon={fasCalendar} />}
						infoTooltip="Ennyi időn belül bekövetkező váratlan esemény esetén fizet a biztosító."
						append="év"
						validate={this.validDurationYears}
						disabled={disabled}
						required
					/>
					<FormCurrency
						label="Milyen összegű biztosítási védelmet szeretnél? (minimum 5 millió Ft)"
						name="riskInsuranceAmount"
						infoTooltip="Az a maximális kártérítési biztosítási összeg, amit kritikus betegség, ill. rokkantság esetén kifizet a biztosító a biztosítottnak, vagy haláseset okán a biztosító kifizet a kedvezményezett(ek) részére."
						validate={this.validRiskInsuranceAmount}
						disabled={disabled}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<ProgressButton
						type="submit"
						icon={<FontAwesomeIcon icon={fasArrowRight} />}
						className={!form.getState().valid ? "disabled" : undefined}
						state={submitState}
					>
						Tovább
					</ProgressButton>
				</Card.Footer>
			</>
		);
	}

	private validBirthYear(birthYear: number): string {
		const actualYear = dayjs().year();

		if (birthYear < 1000) {
			return `Add meg a teljes évszámot!`;
		}

		if (birthYear > actualYear) {
			return `A születési éve nem lehet nagyobb mint ${actualYear}`;
		}

		const age = dayjs().diff(dayjs(`${birthYear}-01-01`), "years");

		if (age < MIN_AGE) {
			return `${MIN_AGE} év alatt sajnos nem tudunk önálló biztosítási konstrukciót ajánlani.`;
		}

		if (age > MAX_AGE) {
			return `${MAX_AGE} évnél idősebb személy nem veheti igénybe a biztosítást!`;
		}
	}

	private validDurationYears(durationYears: number, values: Record<string, any>): string {
		if (durationYears < MIN_DURATION_YEARS) {
			return `A védelem hossza nem lehet kevesebb mint ${MIN_DURATION_YEARS} év!`;
		}

		if (durationYears > MAX_DURATION_YEARS) {
			return `A védelem hossza nem lehet több mint ${MAX_DURATION_YEARS} év!`;
		}

		const { birthYear = 0 } = values;

		if (birthYear) {
			const age = dayjs().diff(dayjs(`${birthYear}-01-01`), "years");

			if (age + durationYears > MAX_INSURED_AGE) {
				return `A szerződés lejáratakor a biztosított legfeljebb ${MAX_INSURED_AGE} éves lehet. Kérjük rövidítsd a tartamot!`;
			}
		}
	}

	private validRiskInsuranceAmount(riskInsuranceAmount: number): string {
		if (riskInsuranceAmount < MIN_RISK_INSURANCE_AMOUNT) {
			return `Kalkulátorunkban a minimális biztosítási összeg ${formatText(
				MIN_RISK_INSURANCE_AMOUNT,
				"ft",
			)}. Növeld a biztosítási összeget!`;
		}

		if (riskInsuranceAmount > MAX_RISK_INSURANCE_AMOUNT) {
			return `Kalkulátorunkban a maximális biztosítási összeg ${formatText(
				MAX_RISK_INSURANCE_AMOUNT,
				"ft",
			)}. Csökkentsd a biztosítási összeget!`;
		}
	}

	protected getComponentName(): string {
		return "Step2";
	}
}
